import React, { useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import { API_BASE_URL } from '../../config';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_TEMPLATES = `${ENDPOINT_HOST}/foodintaketemplate`;

function toIntOrNull(s) {
  if (s === '' || s === null || s === undefined) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n;
}

function kcalFromMacros({ carbs, protein, fat }) {
  const c = Number(carbs);
  const p = Number(protein);
  const f = Number(fat);
  if (!Number.isFinite(c) || !Number.isFinite(p) || !Number.isFinite(f)) return null;
  return (4 * c) + (4 * p) + (9 * f);
}

export default function FoodIntakeTemplateEditPanel({ item, onClose = null }) {
  const [brand, setBrand] = useState(item?.brand ?? '');
  const [name, setName] = useState(item?.name ?? '');
  const [defaultPortion, setDefaultPortion] = useState(String(item?.defaultPortion ?? 100));
  const [unitName, setUnitName] = useState(item?.unitName ?? 'g');
  const [sugars, setSugars] = useState(String(item?.sugars ?? 0));
  const [carbsWithSugar, setCarbsWithSugar] = useState(String(item?.carbsWithSugar ?? 0));
  const [protein, setProtein] = useState(String(item?.protein ?? 0));
  const [fat, setFat] = useState(String(item?.fat ?? 0));
  const [kcal, setKcal] = useState(String(item?.kcal ?? 0));
  const [refUnits, setRefUnits] = useState(String(item?.refUnits ?? 100));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const countedKcal = useMemo(() => {
    const c = toIntOrNull(carbsWithSugar);
    const p = toIntOrNull(protein);
    const f = toIntOrNull(fat);
    if (c === null || p === null || f === null) return null;
    return kcalFromMacros({ carbs: c, protein: p, fat: f });
  }, [carbsWithSugar, protein, fat]);

  const canSubmit = useMemo(() => {
    const nm = (name || '').trim();
    return (
      nm.length > 0 &&
      toIntOrNull(defaultPortion) !== null &&
      String(unitName || '').trim().length > 0 &&
      toIntOrNull(sugars) !== null &&
      toIntOrNull(carbsWithSugar) !== null &&
      toIntOrNull(protein) !== null &&
      toIntOrNull(fat) !== null &&
      toIntOrNull(kcal) !== null &&
      toIntOrNull(refUnits) !== null
    );
  }, [name, defaultPortion, unitName, sugars, carbsWithSugar, protein, fat, kcal, refUnits]);

  const handleCancel = () => {
    if (onClose) onClose(false);
  };

  const handleOk = async () => {
    setError(null);

    const id = item?.id;
    if (id === null || id === undefined) {
      setError('Missing item id.');
      return;
    }

    const nm = (name || '').trim();
    if (!nm) {
      setError('Name is required.');
      return;
    }

    const payload = {
      id,
      brand: (brand || '').trim(),
      name: nm,
      defaultPortion: toIntOrNull(defaultPortion),
      unitName: String(unitName || '').trim(),
      sugars: toIntOrNull(sugars),
      carbsWithSugar: toIntOrNull(carbsWithSugar),
      protein: toIntOrNull(protein),
      fat: toIntOrNull(fat),
      kcal: toIntOrNull(kcal),
      refUnits: toIntOrNull(refUnits),
    };

    if (Object.values(payload).some((v) => v === null)) {
      setError('defaultPortion / sugars / carbs-with-sugar / protein / fat / kcal / refUnits must be integers.');
      return;
    }

    if (!payload.unitName) {
      setError('unitName is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${ENDPOINT_TEMPLATES}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': authToken } : {})
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        if (onClose) onClose(true);
      } else {
        const errorData = await response.text();
        setError(`Failed to update template: ${errorData} (${response.status})`);
      }
    } catch (e) {
      console.error('Failed to update template:', e);
      setError('Failed to update template. Please check your connection.');
    } finally {
      setTimeout(() => setIsSubmitting(false), 800);
    }
  };

  const inputStyle = {
    width: '100%',
    fontSize: '1.05rem',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #ccc',
    boxSizing: 'border-box'
  };

  const buttonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    minWidth: '100px'
  };

  const fieldLabelStyle = {
    fontSize: 12,
    fontWeight: 800,
    color: '#444',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.02em'
  };

  const fieldWrapStyle = {
    display: 'flex',
    flexDirection: 'column'
  };

  const formatMaybe = (v) => {
    if (v === null || v === undefined || !Number.isFinite(v)) return '—';
    return String(Math.round(v));
  };

  return (
    <div style={{
      padding: '10px 0',
      maxWidth: '520px',
      margin: '0 auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}>
      <div>
        <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>✏️ Edit Food Template</h3>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '14px',
            textAlign: 'center',
            border: '1px solid #f5c6cb'
          }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'grid', gap: 12, marginBottom: 14 }}>
          {/* brand, name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Brand</div>
              <input type="text" placeholder="e.g. XXX" value={brand} onChange={(e) => setBrand(e.target.value)} style={inputStyle} autoFocus />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Name</div>
              <input type="text" placeholder="e.g. Skyr" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* ref units, unit name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Reference units</div>
              <input type="number" placeholder="e.g. 100" value={refUnits} onChange={(e) => setRefUnits(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Unit name</div>
              <input type="text" placeholder="e.g. g / ml / pcs" value={unitName} onChange={(e) => setUnitName(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* kcal, fat */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>kcal</div>
              <input type="number" placeholder="kcal" value={kcal} onChange={(e) => setKcal(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Fat</div>
              <input type="number" placeholder="g" value={fat} onChange={(e) => setFat(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* carbs, sugar */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Carbs with sugar</div>
              <input type="number" placeholder="g" value={carbsWithSugar} onChange={(e) => setCarbsWithSugar(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Sugars</div>
              <input type="number" placeholder="g" value={sugars} onChange={(e) => setSugars(e.target.value)} style={inputStyle} />
            </div>
          </div>

          {/* protein, counted kcal */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Protein</div>
              <input type="number" placeholder="g" value={protein} onChange={(e) => setProtein(e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Counted kcal</div>
              <div
                style={{
                  ...inputStyle,
                  borderStyle: 'dashed',
                  borderColor: '#ff8a00',
                  background: '#fff',
                  fontWeight: 800,
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="counted kcal"
                title="counted kcal from macros (4*C + 4*P + 9*F); sugars assumed included in carbs"
              >
                {formatMaybe(countedKcal)}
              </div>
            </div>
          </div>

          {/* empty, default portion */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div />
            <div style={fieldWrapStyle}>
              <div style={fieldLabelStyle}>Default portion</div>
              <input type="number" placeholder="e.g. 100" value={defaultPortion} onChange={(e) => setDefaultPortion(e.target.value)} style={inputStyle} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: 'auto' }}>
        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          style={{
            ...buttonStyle,
            backgroundColor: '#6c757d',
            color: 'white',
            opacity: isSubmitting ? 0.6 : 1
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleOk}
          disabled={isSubmitting || !canSubmit}
          style={{
            ...buttonStyle,
            backgroundColor: '#00a2ff',
            color: 'white',
            opacity: (isSubmitting || !canSubmit) ? 0.6 : 1
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
